import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft, Minus, Plus, ShoppingCart, X, FileDown } from "lucide-react";
import { toast } from "sonner";
import { generateInvoicePdf } from "../../lib/invoicePdf";
import { notifyStockWarnings, stockTone, fmtQty } from "../../lib/stock";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

export default function EditOrder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // {pid: qty}
  const [q, setQ] = useState("");
  const [notes, setNotes] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [storeType, setStoreType] = useState("retail");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [oRes, pRes] = await Promise.all([
          api.get(`/orders/${id}`),
          api.get("/products"),
        ]);
        if (cancelled) return;
        const o = oRes.data;
        setOrder(o);
        setStoreType(o.store_type === "wholesale" ? "wholesale" : "retail");
        setProducts(pRes.data.filter((x) => x.active));
        const initial = {};
        for (const it of o.items || []) initial[it.product_id] = it.quantity;
        setCart(initial);
        setNotes(o.notes || "");
        try {
          const sRes = await api.get(`/stores/${o.store_id}`);
          if (!cancelled) setStore(sRes.data);
        } catch {
          // store lookup not critical for PDF
        }
      } catch (e) {
        toast.error(formatError(e));
        nav("/app/orders", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, nav]);

  const priceOf = (p) =>
    storeType === "wholesale" ? p.wholesale_price : p.retail_price;

  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())),
    [products, q],
  );

  const total = useMemo(() => {
    let t = 0;
    for (const p of products) {
      const qty = cart[p.id] || 0;
      if (qty) t += qty * priceOf(p);
    }
    return t;
  }, [cart, products, storeType]);

  const count = Object.values(cart).reduce((a, b) => a + (b > 0 ? 1 : 0), 0);

  const bump = (pid, delta) => {
    setCart((c) => {
      const q = Math.max(0, (c[pid] || 0) + delta);
      const next = { ...c };
      if (q === 0) delete next[pid];
      else next[pid] = q;
      return next;
    });
  };

  const setQty = (pid, v) => {
    const q = Math.max(0, Number(v) || 0);
    setCart((c) => {
      const next = { ...c };
      if (q === 0) delete next[pid];
      else next[pid] = q;
      return next;
    });
  };

  const submit = async ({ printInvoice = false } = {}) => {
    setSubmitting(true);
    try {
      const items = Object.entries(cart).map(([product_id, quantity]) => ({ product_id, quantity }));
      const { data } = await api.put(`/orders/${id}`, { items, notes, store_type: storeType });
      toast.success(`Order updated · ${fmtINR(data.total)}`);
      notifyStockWarnings(data.stock_warnings);
      if (printInvoice) {
        try {
          await generateInvoicePdf(data, store, { output: "save" });
          toast.success("Invoice PDF downloaded");
        } catch (e) {
          toast.error("Could not generate invoice");
        }
      }
      nav("/app/orders", { replace: true });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !order) return <div className="text-[color:var(--muted)]">Loading…</div>;

  return (
    <div className="pb-24" data-testid="edit-order-page">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-[color:var(--muted)]" data-testid="back-button">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div
          className="inline-flex items-center rounded-full border border-[color:var(--border)] p-0.5 bg-white"
          data-testid="pricing-toggle"
        >
          <button
            type="button"
            onClick={() => setStoreType("retail")}
            className={`text-xs px-3 py-1 rounded-full font-semibold transition ${
              storeType === "retail"
                ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
                : "text-[color:var(--muted)]"
            }`}
            data-testid="pricing-toggle-retail"
          >
            Retail
          </button>
          <button
            type="button"
            onClick={() => setStoreType("wholesale")}
            className={`text-xs px-3 py-1 rounded-full font-semibold transition ${
              storeType === "wholesale"
                ? "bg-[color:var(--mustard)]/25 text-[#9A742E]"
                : "text-[color:var(--muted)]"
            }`}
            data-testid="pricing-toggle-wholesale"
          >
            Wholesale
          </button>
        </div>
      </div>
      {storeType !== order.store_type && (
        <div
          className="mb-3 text-xs px-3 py-2 rounded-lg bg-[color:var(--mustard)]/15 text-[#7A5A1F] border border-[color:var(--mustard)]/30"
          data-testid="pricing-changed-banner"
        >
          Pricing switched from <b>{order.store_type}</b> to <b>{storeType}</b>. Line totals will recalculate on save.
        </div>
      )}

      <p className="overline text-[color:var(--muted)]">Editing order at</p>
      <h1 className="font-display text-2xl font-bold mt-1 mb-1">{order.store_name}</h1>
      <div className="text-xs text-[color:var(--muted)] mb-4">
        Placed {new Date(order.created_at).toLocaleString()}
      </div>

      <Input
        placeholder="Search products…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-12 mb-4"
        data-testid="edit-order-product-search"
      />

      <div className="space-y-2">
        {filtered.map((p) => {
          const qty = cart[p.id] || 0;
          return (
            <div
              key={p.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-[color:var(--border)] bg-white"
              data-testid={`edit-product-item-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium font-tamil truncate">{p.name}</div>
                <div className="text-sm text-[color:var(--muted)] tabular-nums">
                  {fmtINR(priceOf(p))}{" "}
                  <span className="text-xs">
                    · retail {fmtINR(p.retail_price)} · w/s {fmtINR(p.wholesale_price)}
                  </span>
                </div>
                <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${stockTone(p)}`} data-testid={`edit-stock-${p.id}`}>
                  Stock {fmtQty(p.stock_qty)}
                </span>
              </div>
              {qty === 0 ? (
                <Button
                  size="sm"
                  onClick={() => bump(p.id, 1)}
                  className="h-10 bg-[color:var(--ink)] hover:bg-black text-white"
                  data-testid={`edit-add-product-${p.id}`}
                >
                  <Plus className="h-4 w-4" /> Add
                </Button>
              ) : (
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" onClick={() => bump(p.id, -1)} className="h-9 w-9" data-testid={`edit-dec-${p.id}`}>
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    value={qty}
                    onChange={(e) => setQty(p.id, e.target.value)}
                    className="w-14 h-9 text-center"
                    data-testid={`edit-qty-${p.id}`}
                  />
                  <Button size="icon" variant="outline" onClick={() => bump(p.id, 1)} className="h-9 w-9" data-testid={`edit-inc-${p.id}`}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {count > 0 && (
        <div className="fixed bottom-20 inset-x-0 px-4 z-40 max-w-xl mx-auto">
          <button
            onClick={() => setReviewing(true)}
            className="w-full rounded-xl bg-[color:var(--primary)] text-white py-3 flex items-center justify-between px-5 shadow-lg btn-tap"
            data-testid="edit-review-cart-button"
          >
            <span className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              <span className="font-semibold">{count} items</span>
            </span>
            <span className="font-display text-lg font-bold">{fmtINR(total)} →</span>
          </button>
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center" onClick={() => setReviewing(false)}>
          <div
            className="bg-white w-full max-w-xl rounded-t-2xl md:rounded-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-cart-review-sheet"
          >
            <div className="flex items-center justify-between p-4 border-b border-[color:var(--border)]">
              <h2 className="font-display text-xl font-bold">Review changes</h2>
              <button onClick={() => setReviewing(false)} className="text-[color:var(--muted)]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {Object.entries(cart).map(([pid, qty]) => {
                const p = products.find((x) => x.id === pid);
                if (!p) return null;
                return (
                  <div key={pid} className="flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium font-tamil">{p.name}</div>
                      <div className="text-xs text-[color:var(--muted)]">
                        {qty} × {fmtINR(priceOf(p))}
                      </div>
                    </div>
                    <div className="tabular-nums font-medium">{fmtINR(qty * priceOf(p))}</div>
                  </div>
                );
              })}
              <div className="pt-2">
                <Textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-order-notes-input" />
              </div>
            </div>
            <div className="p-4 border-t border-[color:var(--border)] bg-[color:var(--surface-2)]">
              <div className="flex items-center justify-between mb-3">
                <span className="overline">New total</span>
                <span className="font-display text-2xl font-bold">{fmtINR(total)}</span>
              </div>
              <Button
                onClick={() => submit({ printInvoice: false })}
                disabled={submitting || count === 0}
                className="w-full h-14 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white text-base"
                data-testid="save-order-button"
              >
                {submitting ? "Saving…" : "Save changes"}
              </Button>
              <Button
                onClick={() => submit({ printInvoice: true })}
                disabled={submitting || count === 0}
                variant="outline"
                className="w-full h-12 rounded-xl mt-2 border-[color:var(--ink)] text-[color:var(--ink)]"
                data-testid="save-and-invoice-button"
              >
                <FileDown className="h-4 w-4 mr-2" />
                {submitting ? "Saving…" : "Save & download invoice PDF"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
